import { useEffect, useRef } from "react";
import { useUserRefresh } from "./use-user-refresh";
import useAuthStore from "@/store/use-auth-store";

interface UsePageVisibilityOptions {
  /** Minimum süre (ms) refresh'ler arasında */
  minRefreshInterval?: number;
  /** Debounce süresi (ms) multiple event'ler için */
  debounceMs?: number;
  /** Silent refresh yapalım mı */
  silent?: boolean;
}

/**
 * Hook for handling page visibility changes and refreshing user data
 * Stripe payment'tan dönen kullanıcıların user data'sını otomatik refresh eder
 */
export const usePageVisibility = (options: UsePageVisibilityOptions = {}) => {
  const {
    minRefreshInterval = 5000, // 5 saniye
    debounceMs = 100,
    silent = true,
  } = options;

  const { refreshUser } = useUserRefresh();
  const { user } = useAuthStore();
  const lastVisibilityChangeRef = useRef<number>(Date.now());

  useEffect(() => {
    let refreshTimeoutId: NodeJS.Timeout | null = null;

    const handlePageBecomesVisible = async () => {
      // Kullanıcı authenticate olmuşsa ve sayfa görünürse
      if (!document.hidden && user?._id) {
        const timeSinceLastChange =
          Date.now() - lastVisibilityChangeRef.current;

        // Minimum interval geçmişse refresh yap
        if (timeSinceLastChange > minRefreshInterval) {
          if (!silent) {
          }

          try {
            await refreshUser({ silent });
          } catch {
            if (!silent) {
              console.warn("⚠️ Failed to refresh user data on page focus");
            }
          }
        }

        lastVisibilityChangeRef.current = Date.now();
      }
    };

    const debouncedRefresh = () => {
      // Önceki timeout'u iptal et
      if (refreshTimeoutId) {
        clearTimeout(refreshTimeoutId);
      }

      // Debounce - aynı anda gelen multiple event'leri birleştir
      refreshTimeoutId = setTimeout(handlePageBecomesVisible, debounceMs);
    };

    // Sadece visibilitychange kullan (focus event'i gereksiz duplicate)
    document.addEventListener("visibilitychange", debouncedRefresh);

    return () => {
      if (refreshTimeoutId) {
        clearTimeout(refreshTimeoutId);
      }
      document.removeEventListener("visibilitychange", debouncedRefresh);
    };
  }, [user, refreshUser, minRefreshInterval, debounceMs, silent]);

  return {
    // Future: visibility state'i de return edebiliriz
    isVisible: !document.hidden,
  };
};
